import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Cpu, LineChart, Rocket } from 'lucide-react';

import { Logo } from '@/components/common/Logo';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';

/**
 * Split layout: a branded panel on the left (visible on `md+`) sells the
 * product while the right column hosts the active auth form. On small
 * screens the left panel collapses entirely and the form gets the full
 * width with a compact top bar.
 *
 * Visual style mirrors the legacy login (dark left half, gradient
 * accent) while leaning on our design tokens for theme parity. No raw
 * colour literals — only tokens.
 */
export function AuthLayout() {
  const { t } = useTranslation('auth');

  return (
    <div className="relative flex min-h-full bg-background text-foreground">
      {/* ── Left: brand panel (md+) ───────────────────────────────── */}
      <aside className="relative hidden flex-col justify-between overflow-hidden border-r border-border bg-sidebar p-10 text-sidebar-foreground md:flex md:w-[44%] lg:w-2/5">
        {/* Decorative gradient washes — primary, info, success tones at
            low alpha so they read as ambient light, not loud blocks. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_20%_10%,hsl(var(--primary)/0.25),transparent_60%),radial-gradient(50%_50%_at_85%_85%,hsl(var(--info)/0.18),transparent_60%),radial-gradient(40%_40%_at_60%_50%,hsl(var(--success)/0.10),transparent_60%)]"
        />

        {/* Top: logo — centered horizontally on the brand panel. */}
        <div className="relative z-10 flex justify-center">
          <Logo size="lg" />
        </div>

        {/* Middle: tagline + feature list */}
        <div className="relative z-10 max-w-md space-y-8">
          <div>
            <h1 className="text-3xl font-semibold leading-tight tracking-tight lg:text-4xl">
              {t('brandPanel.tagline')}
            </h1>
            <p className="mt-3 text-base text-sidebar-foreground/70">
              {t('brandPanel.subtitle')}
            </p>
          </div>

          <ul className="space-y-3">
            <FeatureRow Icon={LineChart} title={t('brandPanel.features.recommendations')} />
            <FeatureRow Icon={Rocket} title={t('brandPanel.features.deploy')} />
            <FeatureRow Icon={Cpu} title={t('brandPanel.features.monitor')} />
          </ul>
        </div>

        {/* Bottom: support footer */}
        <div className="relative z-10 text-xs text-sidebar-foreground/50">
          {t('brandPanel.footer', { year: new Date().getFullYear() })}
        </div>
      </aside>

      {/* ── Right: form column ────────────────────────────────────── */}
      <main className="relative flex flex-1 flex-col">
        {/* Top bar — compact on auth pages. On mobile the logo appears
            here too so the brand is always visible. */}
        <header className="flex items-center justify-between px-6 py-4">
          <div className="md:hidden">
            <Logo />
          </div>
          <div className="ml-auto flex items-center gap-1">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </header>

        {/* Form content — vertically centered. The Outlet renders the
            mode-aware container from LoginPage (login / signup / forgot
            forms share this surface). */}
        <div className="flex flex-1 items-center justify-center px-6 pb-12">
          <div className="w-full max-w-sm">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

function FeatureRow({
  Icon,
  title,
}: {
  Icon: React.ElementType;
  title: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-success/40 bg-success/10 text-success">
        <CheckCircle2 className="h-3.5 w-3.5" />
      </span>
      <div className="flex items-center gap-2 pt-0.5">
        <Icon className="h-3.5 w-3.5 text-sidebar-foreground/60" />
        <span className="text-sm text-sidebar-foreground/90">{title}</span>
      </div>
    </li>
  );
}
